// Salary slip + bonus slip PDFs (moved out of App.js).
// Paid salary slips print the FINALIZED snapshot, never today's salary/attendance.
import jsPDF from "jspdf";
import { loadLogoBase64 } from "../ui/core";
import { methodLabel, period, fmtDay } from "./ui/tokens";

const rs = (n) => `Rs. ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function header(pdf, logo, title, rightTop, rightBottom) {
    const W = 210, mL = 20, mR = 20;
    pdf.setFillColor(17, 24, 39);
    pdf.rect(0, 0, W, 42, "F");
    // "FAST" compression keeps the 1563px logo from inflating each slip to ~9 MB.
    if (logo) { try { pdf.addImage(logo, "PNG", mL, 8, 22, 22, "fx-logo", "FAST"); } catch (_) {} }
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18); pdf.setFont("helvetica", "bold");
    pdf.text(title, mL + 26, 18);
    pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
    pdf.text("Foxbeep Technology Pvt. Ltd.", mL + 26, 26);
    pdf.text(rightTop, W - mR, 18, { align: "right" });
    pdf.text(rightBottom, W - mR, 26, { align: "right" });
}

function footer(pdf, y, note) {
    const W = 210, mL = 20, mR = 20;
    pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 5;
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(60, 60, 60);
    pdf.text("Foxbeep Technology Pvt. Ltd.", mL, y); y += 5;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(120, 120, 120);
    pdf.text("Madhyapur Thimi, Bhaktapur, Nepal  •  Ph: +977-9705516131", mL, y); y += 4;
    pdf.text("Foxbeeptech@gmail.com  •  Support@foxbeep.com  •  www.foxbeep.com", mL, y); y += 6;
    pdf.setFont("helvetica", "italic"); pdf.setFontSize(8);
    pdf.text(note + " Computer-generated document. Generated: " + new Date().toLocaleDateString(), mL, y);
}

// Label/value row; long values (e.g. bank references) wrap instead of running off the page.
// Returns the extra height used by wrapped lines.
function kv(pdf, y, k, v, x = 20, dx = 35) {
    pdf.setFont("helvetica", "normal"); pdf.text(k, x, y);
    pdf.setFont("helvetica", "bold");
    const lines = pdf.splitTextToSize(String(v ?? "—"), 210 - 20 - (x + dx));
    pdf.text(lines, x + dx, y);
    pdf.setFont("helvetica", "normal");
    return (lines.length - 1) * 5;
}

/**
 * fig: { grossSalary, deduction, netSalary, present, absent, holidays, periods[], daysInMonth, source }
 * employee: { name, designation }
 * pay: { status: "paid"|"pending", amount, payment_method, txn_number, paid_at }
 */
export async function downloadSalarySlip({ year, month, employee, fig, pay }) {
    const logo = await loadLogoBase64();
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, mL = 20, mR = 20, cW = W - mL - mR;
    const paid = pay.status === "paid";
    header(pdf, logo, "SALARY SLIP", period(year, month), paid ? "Status: PAID" : "Status: OUTSTANDING");
    let y = 52;
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.text("Employee Details", mL, y); y += 7;
    pdf.setFontSize(10);
    kv(pdf, y, "Name:", employee?.name); y += 6;
    kv(pdf, y, "Designation:", employee?.designation || "—"); y += 6;
    kv(pdf, y, "Period:", period(year, month)); y += 12;
    pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 8;

    pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.text("Earnings", mL, y); y += 7;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    const periods = fig.periods || [];
    if (periods.length > 1) {
        for (const p of periods) {
            pdf.text(`${rs(p.monthly_salary)}/month × ${p.days} of ${fig.daysInMonth} days`, mL, y);
            pdf.text(rs(p.amount), W - mR, y, { align: "right" }); y += 6;
        }
    }
    pdf.setFont("helvetica", "bold");
    pdf.text(periods.length > 1 ? "Gross Salary (prorated)" : "Gross Monthly Salary", mL, y);
    pdf.text(rs(fig.grossSalary), W - mR, y, { align: "right" }); y += 12;

    pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.text("Deductions", mL, y); y += 7;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    pdf.text(`Absent Days (${fig.absent || 0} day${fig.absent === 1 ? "" : "s"})`, mL, y);
    pdf.setTextColor(180, 0, 0); pdf.text(`- ${rs(fig.deduction)}`, W - mR, y, { align: "right" }); y += 12;
    pdf.setTextColor(30, 30, 30);

    pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.text("Attendance Summary", mL, y); y += 7;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    pdf.text("Present Days:", mL, y); pdf.text(`${fig.present || 0}`, mL + 60, y); y += 6;
    pdf.text("Absent Days:", mL, y); pdf.text(`${fig.absent || 0}`, mL + 60, y); y += 6;
    pdf.text("Holidays:", mL, y); pdf.text(`${fig.holidays || 0}`, mL + 60, y); y += 12;

    pdf.setDrawColor(220, 220, 220); pdf.line(mL, y, W - mR, y); y += 6;
    pdf.setFillColor(245, 255, 245);
    pdf.roundedRect(mL, y, cW, 16, 2, 2, "FD");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(20, 120, 60);
    pdf.text("NET SALARY", mL + 6, y + 10);
    pdf.text(rs(fig.netSalary), W - mR - 6, y + 10, { align: "right" });
    y += 24;

    if (paid) {
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(30, 30, 30);
        pdf.text("Payment Details", mL, y); y += 7; pdf.setFontSize(10);
        kv(pdf, y, "Amount paid:", rs(pay.amount)); y += 6;
        if (pay.payment_method) { kv(pdf, y, "Method:", methodLabel(pay.payment_method)); y += 6; }
        if (pay.txn_number) { y += kv(pdf, y, "Txn #:", pay.txn_number); y += 6; }
        if (pay.paid_at) { kv(pdf, y, "Paid on:", fmtDay(pay.paid_at)); y += 6; }
        y += 4;
    }
    if (!paid && fig.needsReview) {
        pdf.setFont("helvetica", "italic"); pdf.setFontSize(8); pdf.setTextColor(150, 90, 0);
        pdf.text("ESTIMATE ONLY: based on unverified salary history. Not a confirmed payroll figure.", mL, y); y += 6;
    }
    if (fig.source === "legacy" && paid) {
        pdf.setFont("helvetica", "italic"); pdf.setFontSize(8); pdf.setTextColor(150, 90, 0);
        pdf.text("LEGACY payment (before payroll finalization): breakdown recalculated from current records, not a finalized snapshot.", mL, y); y += 6;
    }
    y += 4;
    footer(pdf, y, paid ? "Payment released." : "Payment pending.");
    pdf.save(`salary_slip_${period(year, month).replace(" ", "_")}.pdf`);
}

export async function downloadBonusSlip({ bonus, employee }) {
    const b = bonus;
    const logo = await loadLogoBase64();
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, mL = 20, mR = 20, cW = W - mL - mR;
    const paid = b.status === "paid";
    header(pdf, logo, "BONUS SLIP", b.month ? period(b.year, b.month) : "", paid ? "Status: PAID" : "Status: PENDING");
    let y = 52;
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.text("Employee Details", mL, y); y += 7;
    pdf.setFontSize(10);
    kv(pdf, y, "Name:", employee?.name); y += 6;
    kv(pdf, y, "Designation:", employee?.designation || "—"); y += 12;
    pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.3); pdf.line(mL, y, W - mR, y); y += 8;
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.text("Bonus Details", mL, y); y += 7;
    pdf.setFontSize(10);
    if (b.reason) { y += kv(pdf, y, "Reason:", b.reason); y += 6; }
    kv(pdf, y, "Date:", b.date || "—"); y += 10;
    pdf.line(mL, y, W - mR, y); y += 6;
    pdf.setFillColor(245, 255, 245);
    pdf.roundedRect(mL, y, cW, 16, 2, 2, "FD");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(20, 120, 60);
    pdf.text("BONUS AMOUNT", mL + 6, y + 10);
    pdf.text(rs(b.amount), W - mR - 6, y + 10, { align: "right" });
    y += 24;
    if (paid) {
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(30, 30, 30);
        pdf.text("Payment Details", mL, y); y += 7; pdf.setFontSize(10);
        kv(pdf, y, "Amount paid:", rs(b.paid_amount ?? b.amount)); y += 6;
        if (b.payment_method) { kv(pdf, y, "Method:", methodLabel(b.payment_method)); y += 6; }
        if (b.txn_number) { y += kv(pdf, y, "Txn #:", b.txn_number); y += 6; }
        if (b.paid_at) { kv(pdf, y, "Paid on:", fmtDay(b.paid_at)); y += 6; }
    }
    y += 8;
    footer(pdf, y, paid ? "Payment released." : "Payment pending.");
    pdf.save(`bonus_slip_${b.month ? period(b.year, b.month).replace(" ", "_") : b.id}.pdf`);
}
